import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Trash2, Upload, GalleryHorizontal, Star } from 'lucide-react';
import { ref as dbRef, set, remove, push } from 'firebase/database';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';

// Initialize storage outside the component
const storage = getStorage();

// Enum for upload types to reuse logic
const UPLOAD_TYPE = {
  GALLERY: 'GALLERY',
  HERO: 'HERO',
};

export default function MediaManager({ db, showToast, gallery, heroImageUrl }) {
  const [uploadingHero, setUploadingHero] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const galleryFileInputRef = useRef(null);
  const heroFileInputRef = useRef(null);

  const handleUpload = async (file, type) => {
    if (!file) return;

    const isHero = type === UPLOAD_TYPE.HERO;
    const setUploading = isHero ? setUploadingHero : setUploadingGallery;
    
    setUploading(true);
    
    try {
      let storagePath, dbPath;

      if (isHero) {
        // Hero image uses a fixed name so it's always overwritten
        storagePath = `hero/hero_image.jpg`;
        dbPath = 'content/heroImage';
      } else {
        // Gallery images use unique names and push keys
        storagePath = `gallery/${Date.now()}-${file.name}`;
        dbPath = 'content/gallery';
      }
      
      // 1. Upload to Firebase Storage
      const storageReference = storageRef(storage, storagePath);
      await uploadBytes(storageReference, file);
      const downloadUrl = await getDownloadURL(storageReference);

      // 2. Save URL to Realtime Database
      if (isHero) {
        await set(dbRef(db, dbPath), downloadUrl); // Set fixed value
      } else {
        const newImageRef = push(dbRef(db, dbPath));
        await set(newImageRef, {
          url: downloadUrl,
          uploadedAt: new Date().toISOString()
        });
      }

      showToast(`${isHero ? 'Hero Image' : 'Gallery Image'} uploaded successfully!`);
      // Reset file input
      (isHero ? heroFileInputRef : galleryFileInputRef).current.value = '';
    } catch (err) {
      console.error(err);
      showToast('Error uploading image', 'error');
    }
    setUploading(false);
  };

  // IMPORTANT FIX: Use the actual Firebase key for deletion, not the array index.
  const handleDeleteGalleryImage = async (imageKey) => {
    if (window.confirm('Are you sure you want to delete this gallery image?')) {
      try {
        // 1. Delete from Realtime Database
        await remove(dbRef(db, `content/gallery/${imageKey}`));
        
        // NOTE: Storage cleanup (deleteObject) is complex and often skipped in simple examples
        // but it is recommended for production apps to avoid orphan files.

        showToast('Gallery image deleted successfully!');
      } catch (err) {
        console.error('Deletion error:', err);
        showToast('Error deleting image', 'error');
      }
    }
  };

  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      
      {/* --- 1. HERO IMAGE UPLOAD SECTION --- */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-[#16161b] rounded-lg shadow-xl p-8 border-l-4 border-[#bd9f67]"
      >
        <h3 className="text-2xl font-semibold text-[#f9f9f9] mb-6 flex items-center gap-2">
          <Star size={24} className="text-[#bd9f67]" /> Main Hero Image Uploader
        </h3>

        {heroImageUrl && (
          <div className="mb-6 p-4 bg-[#1e1e25] rounded-lg flex items-center space-x-4">
            <p className="font-medium text-[#c9c9d4]">Current Hero Image Preview:</p>
            <div
              className="w-32 h-20 bg-[#33333d] bg-cover bg-center rounded-lg shadow-inner"
              style={{ backgroundImage: `url(${heroImageUrl})` }}
            />
          </div>
        )}

        <div className="border-2 border-dashed border-[#bd9f67]/30 rounded-lg p-8 text-center">
          <input
            type="file"
            accept="image/*"
            ref={heroFileInputRef}
            onChange={(e) => {
              if (e.target.files[0]) {
                handleUpload(e.target.files[0], UPLOAD_TYPE.HERO);
              }
            }}
            className="hidden"
          />

          <button
            onClick={() => heroFileInputRef.current?.click()}
            disabled={uploadingHero}
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#bd9f67] hover:bg-[#d0b07a] text-[#243137] rounded-lg font-bold transition disabled:opacity-50"
          >
            <Upload size={20} /> {uploadingHero ? 'Uploading...' : 'Upload/Replace Hero Image'}
          </button>
          <p className="text-[#a6a6b5] mt-4 text-sm">This will overwrite the current main background image.</p>
        </div>
      </motion.div>

      {/* --- 2. GALLERY IMAGE UPLOAD SECTION --- */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-[#16161b] rounded-lg shadow-xl p-8 border-l-4 border-[#bd9f67]"
      >
        <h3 className="text-2xl font-semibold text-[#f9f9f9] mb-6 flex items-center gap-2">
          <GalleryHorizontal size={24} /> Carousel/Gallery Upload
        </h3>

        <div className="border-2 border-dashed border-[#bd9f67]/30 rounded-lg p-8 text-center">
          <input
            type="file"
            accept="image/*"
            ref={galleryFileInputRef}
            onChange={(e) => {
              if (e.target.files[0]) {
                handleUpload(e.target.files[0], UPLOAD_TYPE.GALLERY);
              }
            }}
            className="hidden"
          />

          <button
            onClick={() => galleryFileInputRef.current?.click()}
            disabled={uploadingGallery}
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#bd9f67] hover:bg-[#d0b07a] text-[#243137] rounded-lg font-bold transition disabled:opacity-50"
          >
            <Upload size={20} /> {uploadingGallery ? 'Uploading...' : 'Add New Gallery Image'}
          </button>

          <p className="text-[#a6a6b5] mt-4 text-sm">Upload images for the scrolling carousel/gallery.</p>
        </div>
      </motion.div>

      {/* --- 3. GALLERY GRID DISPLAY --- */}
      <div>
        <h3 className="text-2xl font-semibold text-[#f9f9f9] mb-6">
          Gallery Images ({gallery.length})
        </h3>

        {gallery.length === 0 ? (
          <p className="text-center text-[#7d7d8a] py-8">No images yet. Upload one to get started!</p>
        ) : (
          <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {gallery.map((image, index) => (
              <motion.div
                key={image.key || index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.05 }}
                className="relative group rounded-lg overflow-hidden shadow-lg border border-[#bd9f67]/20 hover:border-[#bd9f67]/50 hover:shadow-[0_0_25px_rgba(189,159,103,0.18)] transition"
              >
                <img
                  src={image.url}
                  alt={`Gallery ${index}`}
                  className="w-full h-40 object-cover"
                />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex flex-col items-center justify-center p-2">
                  <p className="text-white text-xs mb-2 truncate w-full text-center">{image.key}</p>
                  <button
                    onClick={() => handleDeleteGalleryImage(image.key)}
                    className="flex items-center gap-1 px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition font-medium text-sm"
                  >
                    <Trash2 size={16} /> Delete
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}